/**
 * Pharmacist API — paper-Rx digitisation queue (Rx-12).
 *
 * Triage (start-review / reject) plus the full digitise step: the pharmacist views the patient's
 * uploaded file, transcribes the medicines, attributes the Rx to an active doctor — adding the
 * doctor to the master first when they aren't in it — and submits.
 */

import { apiFetch, fetchBlobUrl, newIdempotencyKey } from "./client";
import type { RxLineInput } from "./doctor";

/** Someone on an upload: the account holder who sent it, or the family member it is for. */
export interface UploadPerson {
  name: string | null;
  phone: string | null; // the account holder's only
  relationship: string | null; // family member only
  age: number | null; // whole years, when the date of birth is on file
  gender: string | null;
}

/** A row of the digitisation queue. */
export interface Upload {
  id: string;
  patient_account_id: string;
  for_family_member_id: string | null;
  image_r2_key: string;
  note: string | null;
  status: string; // pending_review | in_review | digitized | rejected | cancelled
  reject_reason: string | null;
  reject_note: string | null;
  digitized_prescription_id: string | null;
  created_at: string;
  uploaded_by: UploadPerson; // the patient account holder
  for_member: UploadPerson | null; // null = the holder themself
  reviewed_by_name: string | null; // the pharmacist who last acted on it
  reviewed_at: string | null;
  digitized_prescription_ref: string | null; // e.g. RX-12
  digitized_today: number; // the account's successful digitisations today …
  digitize_daily_limit: number; // … and the daily cap past which digitising is refused
}

export interface PaginatedUploads {
  data: Upload[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface UploadQueueQuery {
  status?: string[]; // none = still to do (pending_review + in_review)
  q?: string; // patient / family member name, note, Rx ref, upload id start, or 3+ phone digits
  reviewer?: "me" | "unassigned";
  for_whom?: "self" | "family";
  uploaded_from?: string; // ISO instant, inclusive
  uploaded_to?: string; // ISO instant, exclusive
  sort?: "created_at" | "patient_name";
  descending?: boolean; // the API defaults to true (newest first)
  page?: number;
  per_page?: number;
}

export interface DoctorOption {
  id: string;
  full_name: string;
  specialisation: string | null;
  /** To tell same-named doctors apart. */
  state_medical_council?: string | null;
  license_number_masked?: string | null;
  phone_masked?: string | null;
  /** An admin verified the doctor; a pharmacist-added doctor isn't, until then (D-17). */
  verified?: boolean;
}

/** A doctor missing from the master, as printed on the paper Rx (PHA-12). */
export interface NewDoctorInput {
  full_name: string;
  phone: string;
  state_medical_council: string;
  license_number: string;
  specialisation?: string | null;
}

/** The digitisation queue, searched, filtered, sorted and paged by the server. */
export const listDigitiseQueue = (query: UploadQueueQuery = {}, signal?: AbortSignal): Promise<PaginatedUploads> => {
  const params = new URLSearchParams();
  if (query.status?.length) params.set("status", query.status.join(","));
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.reviewer) params.set("reviewer", query.reviewer);
  if (query.for_whom) params.set("for_whom", query.for_whom);
  if (query.uploaded_from) params.set("uploaded_from", query.uploaded_from);
  if (query.uploaded_to) params.set("uploaded_to", query.uploaded_to);
  if (query.sort) params.set("sort", query.sort);
  if (query.descending !== undefined) params.set("descending", String(query.descending));
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/pharmacist/uploads?${params.toString()}`, { signal });
};

export const getUpload = (id: string): Promise<Upload> => apiFetch(`/api/v1/pharmacist/uploads/${id}`);

export const startReview = (id: string): Promise<Upload> =>
  apiFetch(`/api/v1/pharmacist/uploads/${id}/start-review`, { method: "POST" });

export const rejectUpload = (id: string, reason: string, note: string | null): Promise<Upload> =>
  apiFetch(`/api/v1/pharmacist/uploads/${id}/reject`, { method: "POST", body: { reason, note } });

/** Active doctors to attribute a digitised Rx to; `q` matches the name, or 3+ phone digits. */
export const listActiveDoctors = (q = "", signal?: AbortSignal): Promise<DoctorOption[]> =>
  apiFetch(`/api/v1/pharmacist/doctors${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`, { signal });

/** Add a doctor missing from the master. They aren't told, and can't sign in until an admin turns
 *  login on. A doctor already listed with that phone is a 409 DOCTOR_ALREADY_EXISTS whose details
 *  carry their `doctor_id`, `full_name` and whether they can be `selectable`. */
export const addDoctor = (body: NewDoctorInput, idempotencyKey?: string): Promise<DoctorOption> =>
  apiFetch("/api/v1/pharmacist/doctors", { method: "POST", body, idempotencyKey: idempotencyKey ?? newIdempotencyKey() });

/** Fetch the uploaded file as a displayable object URL (auth header can't ride a plain src). */
export const fetchUploadImage = (id: string): Promise<{ url: string; contentType: string; filename: string }> =>
  fetchBlobUrl(`/api/v1/pharmacist/uploads/${id}/image`);

export interface DigitiseInput {
  for_family_member_id?: string | null;
  notes?: string | null;
  /** Days the Rx stays orderable, counted on the server; omitted = the class maximum (D-08). */
  validity_days?: number | null;
  lines: RxLineInput[];
}

/** Maximum validity in days per schedule class ("none" = OTC, "H", "H1", "X"). */
export const getRxValidity = (): Promise<{ validity_days: Record<string, number> }> =>
  apiFetch("/api/v1/pharmacist/rx-validity");

/** Create a real Rx from the upload, attributed to `doctorId`. Returns the new prescription. */
export const digitiseUpload = (
  id: string,
  doctorId: string,
  body: DigitiseInput,
  idempotencyKey?: string,
): Promise<{ id: string }> =>
  apiFetch(
    `/api/v1/pharmacist/uploads/${id}/digitize?on_behalf_of_doctor_id=${encodeURIComponent(doctorId)}`,
    { method: "POST", body, idempotencyKey: idempotencyKey ?? newIdempotencyKey() },
  );
